import * as React from "react";
import { Download } from "lucide-react";
import { Button, Dialog, Tooltip } from "@/components/ui";
import { cn } from "@/lib/utils";
import { downloadImage } from "../download";

/** A round download button, floated over an image. */
export const DownloadFab: React.FC<{ url: string; name: string; className?: string }> = ({ url, name, className }) => {
  const [busy, setBusy] = React.useState(false);
  const onClick = async () => {
    setBusy(true);
    try {
      await downloadImage(url, name);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button
            variant="outline"
            size="icon-lg"
            aria-label="Download image"
            loading={busy}
            onClick={() => void onClick()}
            className={cn("rounded-full border-slate-200 bg-white/95 text-slate-700 shadow-md", className)}
            data-testid="download-image"
          />
        }
      >
        <Download aria-hidden="true" />
      </Tooltip.Trigger>
      <Tooltip.Content>Download</Tooltip.Content>
    </Tooltip>
  );
};

interface ImageDialogProps {
  image: { url: string; title: string; fileName: string } | null;
  onClose(): void;
}

/** One image at a readable size, with a way to save it. */
export const ImageDialog: React.FC<ImageDialogProps> = ({ image, onClose }) => (
  <Dialog open={!!image} onOpenChange={next => !next && onClose()} size="lg" showCloseButton>
    {image && (
      <>
        <Dialog.Header className="pr-12">
          <Dialog.Title className="text-base">{image.title}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="relative flex justify-center bg-slate-50" data-testid="image-dialog">
          <img src={image.url} alt={image.title} className="max-h-[70vh] w-auto rounded-lg object-contain" />
          <DownloadFab url={image.url} name={image.fileName} className="absolute right-6 bottom-6" />
        </Dialog.Content>
      </>
    )}
  </Dialog>
);
